/* eslint-disable import/prefer-default-export */
/* eslint-disable class-methods-use-this */
import axios from 'axios';
import qs from 'qs';

/**
 * Auth class
 */
export class Auth {
  /**
   * @param {Object} options - Options
   * @param {string} options.baseUrl - The base URL
   * @param {string} options.clientId - The client ID
   * @param {string} options.secret - The client secret
   * @param {number} options.timeout - The timeout
   */
  constructor(options) {
    const defaults = {
      baseUrl: '',
      client_id: options.clientId,
      client_secret: options.secret,
      timeout: 30000,
    };
    this.options = Object.assign(defaults, options);
    this.onError = null;

    if (this.options.baseUrl.length === 0) {
      throw this.options;
    }
    this.baseUrl = this.options.baseUrl;
    this.localStorage = window.localStorage;
    this.$http = axios.create({
      baseURL: this.options.baseUrl,
      timeout: this.options.timeout,
    });
  }

  /**
   * Register a new user
   *
   * @param {string} username - The username
   * @param {string} email - The email
   * @param {string} password - The password
  * @return {Promise<AxiosResponse>} returns axios response
   *
   * @example
   *
   *     register('joe@soap.com', ''joe@soap.com', 'Password01')
   */
  async register(username, email, password) {
    return this.$http.post('/api/account/register', {
      Username: username,
      Password: password,
      Email: email,
    });
  }

  /**
   * Register a new user user with an organization role.
   *
   * @param {string} username - The username
   * @param {string} email - The email
   * @param {string} password - The password
   * @param {string} role - The role
   * @return {Promise<AxiosResponse>} returns axios response
   *
   * @example
   *
   *     registerWithRole('joe@soap.com', ''joe@soap.com', 'Password01', 'AuroraAdmins')
   */
  async registerWithRole(username, email, password, role) {
    return this.$http.post('/api/account/register', {
      Username: username,
      Password: password,
      Email: email,
      Role: role,
    });
  }

  /**
   * Request for a password reset.
   *
   * @param {string} email - The email address to reset the password for.
   * @return {Promise<AxiosResponse>} returns axios response
   *
   * @example
   *
   *     forgot('joe@soap.com')
   */
  async forgot(email) {
    return this.$http.post('/api/account/forgotpassword', {
      email,
    });
  }

  /**
   * Login with a username and password
   *
   * @param {string} username - The username
   * @param {string} password - The password
   * @return {Promise} returns axios promise
   *
   * @example
   *
   *     login('joe@soap.com', 'Password01')
   */
  async login(username, password) {
    const { data } = await this.$http.post('/token', qs.stringify({
      username,
      password,
      client_id: this.options.client_id,
      client_secret: this.options.client_secret,
      grant_type: 'password',
    }));
    this.localStorage.setItem('auth-token', JSON.stringify(data));
  }

  /**
   * Login with two factor authentication
   *
   * @param {string} username - The username
   * @param {string} password - The password
   * @param {string} code - The two factor code
   * @return {Promise} returns axios promise
   *
   * @example
   *
   *     loginTwoFactor('joe@soap.com', 'Password01', '123456')
   */
  async loginTwoFactor(username, password, code) {
    const tokenRequest = {
      username,
      password,
      code,
      client_id: this.options.client_id,
      client_secret: this.options.client_secret,
      grant_type: 'password',
    };
    const { data } = await this.$http.post('/token', qs.stringify(tokenRequest));
    this.localStorage.setItem('auth-token', JSON.stringify(data));
  }

  /**
   * Login with an external token
   *
   * @param {Object} tokenRequest - The token request
   * @param {string} tokenRequest.externalAccessToken - The external access token
   * @param {string} tokenRequest.provider - The provider
   * @return {Promise<AxiosResponse>} returns axios response
   *
   * @example
   *
   *     loginExternal({
   *       externalAccessToken: '123456',
   *       provider: 'GitHub'
   *     })
   */
  async loginExternal(tokenRequest) {
    return this.$http.get(`/api/account/ObtainLocalAccessToken?externalAccessToken=${tokenRequest.externalAccessToken}&provider=${tokenRequest.provider}`)
      .then((response) => {
        this.saveAuth(response.data);
        this.fillAuth();
        return response;
      });
  }

  logoff() {
    this.localStorage.removeItem('auth-token');
  }

  /**
   * Refresh the token
   */
  async refreshToken() {
    try {
      const oldToken = this.getToken();
      const tokenRequest = {
        grant_type: 'refresh_token',
        refresh_token: oldToken?.refresh_token,
        client_id: this.options.client_id,
        client_secret: this.options.client_secret,
      };
      const { data } = await this.$http.post('/token', qs.stringify(tokenRequest));
      this.localStorage.setItem('auth-token', JSON.stringify(data));
    } catch (err) {
      this.handleErr(err);
    }
  }

  /**
   * Get the local token
   * @return {Object} returns the local token
   */
  getToken() {
    return JSON.parse(this.localStorage.getItem('auth-token'));
  }

  /**
   * Get the access token (refreshes if needed)
   * @return {string} returns the access token
   */
  async getAccessToken() {
    try {
      let token = this.getToken();
      if (token && this.isExpired(token['.expires'])) {
        await this.refreshToken();
        token = this.getToken();
      }
      return token?.access_token;
    } catch (err) {
      this.handleErr(err);
      return undefined;
    }
  }

  /**
   * Handle errors
   * @param {Error} err - The error object
   */
  handleErr(err) {
    if (this.onError) {
      this.onError(err);
    } else {
      console?.log(err);
      throw err;
    }
  }

  /**
   * Check if the token is expired
   * @param {string} date - The expiry date
   * @return {boolean} returns true if the token is expired
   */
  isExpired(date) {
    const today = new Date();
    const dateToCheck = new Date(date);
    if (dateToCheck < today) {
      return true;
    }
    return false;
  }
}
